import type { BimPriceAnalyticsData } from 'gql/graphql';
import { BimPriceAnalyticsItemStatus } from 'gql/graphql';
import { cnMixSpace, Text } from 'uikit';
import { formatNumberToRu } from 'utils';

import type { ICellRendererParams, RowClassRules } from 'ag-grid-community';

import type { TBaseRowData, TRow } from './types';

import Styles from './analytics.module.css';

export type IAnalyticsCellRenderer = {
    fieldName: keyof BimPriceAnalyticsData['topPriceValue'];
    fractionDigits: number | null;
};

export const AnalyticsCellRenderer = (params: IAnalyticsCellRenderer & ICellRendererParams<any>) => {
    const { data, fieldName, fractionDigits = null } = params;

    const isViewChanged = data?.changedProperties?.includes(fieldName);
    const isRemovedPrice = data?.status === BimPriceAnalyticsItemStatus.Removed;
    const withFormat = fractionDigits !== null;

    const topCellValue = isRemovedPrice
        ? data.topPriceValue?.[fieldName] ?? data.refPriceValue?.[fieldName]
        : data.topPriceValue?.[fieldName];

    const refCellValue = data.refPriceValue?.[fieldName];

    return (
        <div className={Styles.cell}>
            <Text
                view={isViewChanged ? 'warning' : 'primary'}
                lineHeight="l"
                className={cnMixSpace({
                    mB: 'xs',
                })}
            >
                {withFormat ? formatNumberToRu(topCellValue, fractionDigits) : topCellValue}
            </Text>
            {!isRemovedPrice && (
                <Text view={isViewChanged ? 'warning' : 'secondary'} size="s">
                    {withFormat ? formatNumberToRu(refCellValue, fractionDigits) : refCellValue}
                </Text>
            )}
        </div>
    );
};

AnalyticsCellRenderer.displayName = 'AnalyticsCellRenderer';

export const stylesRules: RowClassRules = {
    topPinnedRowClass: ({ data }: { data: TBaseRowData }) =>
        Boolean(data && 'topPinnedRow' in data && data?.topPinnedRow),
    boldRow: ({ data }: { data: TRow }) => Boolean(data && 'isSection' in data && data?.isSection),
    semiboldRow: ({ data }: { data: TRow }) => Boolean(data && 'isSubsection' in data && data?.isSubsection),
    addedRowClass: ({ data }: { data: TRow }) =>
        Boolean(data && 'status' in data && data?.status === BimPriceAnalyticsItemStatus.Added),
    changedRowClass: ({ data }: { data: TRow }) =>
        Boolean(data && 'status' in data && data?.status === BimPriceAnalyticsItemStatus.Changed),
    removedRowClass: ({ data }: { data: TRow }) =>
        Boolean(data && 'status' in data && data?.status === BimPriceAnalyticsItemStatus.Removed),
    replacedRowClass: ({ data }: { data: TRow }) =>
        Boolean(data && 'status' in data && data?.status === BimPriceAnalyticsItemStatus.Replaced),
};
